<template>
    <div ref="root" class="week-grid">
        <p v-if="loading" class="week-grid__status">Считаем…</p>
        <p v-else-if="error" class="week-grid__status">{{ error }}</p>
        <template v-else-if="week">
            <p class="week-grid__title">Запросы SSP за последние 7 дней</p>
            <div class="week-grid__scroll">
                <table>
                    <thead>
                        <tr>
                            <th></th>
                            <th
                                v-for="hour in hours"
                                :key="hour"
                                :class="{ 'is-now': hour === nowSlot.hour }"
                            >{{ hourLabel(hour) }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(label, day) in dayLabels" :key="label">
                            <th>{{ label }}</th>
                            <td
                                v-for="hour in hours"
                                :key="hour"
                                :class="{ 'is-now': day === nowSlot.day && hour === nowSlot.hour }"
                            >{{ cell(day, hour) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </template>
    </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';
import axios from 'axios';
import { extractErrorMessage } from '../../utils/httpErrors';
import { rtbWeekSlot } from '../../utils/timezones';

const dayLabels = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const hours = Array.from({ length: 24 }, (_, hour) => hour);
const numberFormat = new Intl.NumberFormat('ru-RU');
const weekCache = new Map();

const props = defineProps({
    gid: { type: String, required: true },
});

const root = ref(null);
const loading = ref(false);
const error = ref('');
const week = ref(weekCache.get(props.gid) ?? null);
const now = ref(new Date());
let clock = null;

const nowSlot = computed(() => rtbWeekSlot(now.value));
const hourLabel = (hour) => String(hour).padStart(2, '0');

const cell = (day, hour) => {
    const value = week.value?.[day]?.[hour];
    if (value === null || value === undefined) return '–';
    return numberFormat.format(value);
};

const reveal = () => {
    nextTick(() => {
        root.value?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
};

const ensureWeek = async () => {
    if (weekCache.has(props.gid)) {
        week.value = weekCache.get(props.gid);
        error.value = '';
        reveal();
        return;
    }

    loading.value = true;
    error.value = '';
    try {
        const { data } = await axios.post('/screens/request-hours', {
            gids: [props.gid],
            week: true,
        });
        const grid = data?.screens?.[0]?.week;
        if (!Array.isArray(grid)) {
            error.value = 'Не удалось загрузить показы';
            return;
        }
        weekCache.set(props.gid, grid);
        week.value = grid;
    } catch (err) {
        error.value = extractErrorMessage(err, 'Не удалось загрузить показы');
    } finally {
        loading.value = false;
        reveal();
    }
};

onMounted(() => {
    now.value = new Date();
    clock = setInterval(() => {
        now.value = new Date();
    }, 30_000);
    ensureWeek();
});

onUnmounted(() => {
    if (clock) clearInterval(clock);
});
</script>

<style scoped lang="scss">
.week-grid {
    min-width: 0;
    color: #111827;
}

.week-grid__title,
.week-grid__status {
    margin: 0 0 8px;
    font-size: 12px;
    font-weight: 600;
}

.week-grid__status {
    font-weight: 400;
}

.week-grid__scroll {
    width: 100%;
    max-width: 100%;
    overflow-x: auto;
}

.week-grid table {
    border-collapse: collapse;
    white-space: nowrap;
}

.week-grid th,
.week-grid td {
    padding: 2px 3px;
    font-size: 10px;
    line-height: 1.2;
    text-align: right;
    border-bottom: 1px solid #f3f4f6;
}

.week-grid th:first-child,
.week-grid td:first-child,
.week-grid tbody th {
    text-align: left;
    position: sticky;
    left: 0;
    background: #fff;
}

.week-grid th.is-now,
.week-grid td.is-now {
    font-weight: 700;
    color: #40bcff;
}
</style>
